import { memo, useMemo } from 'react';
import { Checkbox, FormControlLabel, Box, ToggleButtonGroup, ToggleButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import ChipsSelectEditor from './ChipsSelectEditor';

interface BooleanToggleLabels {
    accept: string;
    refuse: string;
    noPreference: string;
}

interface CheckboxEditorProps {
    ruleId: string;
    value: string | string[] | number | null;
    values: FieldValue[];
    onChange: (value: string | string[]) => void;
    compact?: boolean;
    // Overrides the 3-way toggle labels (default: opt-in accepts/refuses/noPreference) for fields
    // that reuse the same boolean tri-state pattern with different wording, e.g. subscriptions.
    booleanLabels?: BooleanToggleLabels;
    // One or more named items (e.g. subscription types), each rendered with its own label and its
    // own independent tri-state toggle. Selections are stored as "<name>:<selection>" entries in
    // the rule's value array, so each item's choice stays tied to that item.
    subscriptionOptions?: FieldValue[];
}

const BOOLEAN_ACCEPT_VALUE = '1';
const BOOLEAN_REFUSE_VALUE = '0';
const BOOLEAN_NO_PREFERENCE_VALUE = '';
// Distinct from BOOLEAN_NO_PREFERENCE_VALUE (which is falsy and thus indistinguishable from "no
// selection yet" once encoded in a "<name>:<selection>" entry): this is the per-subscription
// toggle's explicit "no response" pick, so it stays a real choice the user can make and later undo,
// instead of being silently dropped like an empty string would be.
const SUBSCRIPTION_NO_PREFERENCE_VALUE = 'no_preference';

// Defined once at module scope: MUI/emotion recomputes and re-injects a component's styles whenever
// it receives a new `sx` object reference, so inlining these literals in the JSX below regenerated
// them (and reprocessed the styles) on every keystroke anywhere in the form, which is what caused
// the perceived input lag on this toggle group.
const ACCEPT_BUTTON_SX = {
    cursor: 'pointer',
    '&:hover': { bgcolor: 'rgba(40, 167, 69, 0.12)' },
    '&.Mui-selected': { bgcolor: '#28a745', color: 'white', '&:hover': { bgcolor: '#218838' } },
};
const REFUSE_BUTTON_SX = {
    cursor: 'pointer',
    '&:hover': { bgcolor: 'rgba(220, 53, 69, 0.12)' },
    '&.Mui-selected': { bgcolor: '#dc3545', color: 'white', '&:hover': { bgcolor: '#c82333' } },
};
const NO_PREFERENCE_BUTTON_SX = {
    cursor: 'pointer',
    '&:hover': { bgcolor: 'rgba(85, 110, 230, 0.12)' },
    '&.Mui-selected': { bgcolor: '#556ee6', color: 'white', '&:hover': { bgcolor: '#4457c9' } },
};
const COMPACT_GRID_SX = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1 };
const TOOLTIP_LABEL_SX = { display: 'flex', alignItems: 'center', gap: 0.5 };
const TOOLTIP_ICON_SX = { fontSize: '1rem', color: 'text.secondary' };
const FORM_CONTROL_LABEL_SX = { m: 0 };

function CheckboxEditor({ ruleId, value, values, onChange, compact = false, booleanLabels, subscriptionOptions }: CheckboxEditorProps) {
    const { t } = useLanguage();
    const selectedValues = useMemo(
        () => (Array.isArray(value) ? value : (value ? [String(value)] : [])),
        [value]
    );
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    if (subscriptionOptions && subscriptionOptions.length > 0) {
        const labels = booleanLabels ?? {
            accept: t('pages.clients.queryBuilder.simple.optin.accepts'),
            refuse: t('pages.clients.queryBuilder.simple.optin.refuses'),
            noPreference: t('pages.clients.queryBuilder.simple.optin.noPreference'),
        };
        const selectionByName = new Map<string, string>();
        selectedValues.forEach(entry => {
            const separatorIndex = entry.indexOf(':');
            if (separatorIndex === -1) return;
            selectionByName.set(entry.slice(0, separatorIndex), entry.slice(separatorIndex + 1));
        });
        const handleSelect = (optionName: string, newValue: string | null) => {
            const next = selectedValues.filter(entry => !entry.startsWith(`${optionName}:`));
            // exclusive ToggleButtonGroup reports newValue === null when the already-selected
            // button is clicked again, so this also handles unchecking back to "no selection".
            if (newValue !== null) next.push(`${optionName}:${newValue}`);
            onChange(next);
        };
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 1.5 }}>
                {subscriptionOptions.map(option => (
                    <Box key={option.name} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#212529', fontWeight: 700 }}>
                            {option.label}
                        </Typography>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={selectionByName.get(option.name) ?? null}
                            onChange={(_event, newValue) => handleSelect(option.name, newValue)}
                        >
                            <ToggleButton value={BOOLEAN_ACCEPT_VALUE} sx={ACCEPT_BUTTON_SX}>
                                {labels.accept}
                            </ToggleButton>
                            <ToggleButton value={BOOLEAN_REFUSE_VALUE} sx={REFUSE_BUTTON_SX}>
                                {labels.refuse}
                            </ToggleButton>
                            <ToggleButton value={SUBSCRIPTION_NO_PREFERENCE_VALUE} sx={NO_PREFERENCE_BUTTON_SX}>
                                {labels.noPreference}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                ))}
            </Box>
        );
    }

    if (values.length === 0) {
        const labels = booleanLabels ?? {
            accept: t('pages.clients.queryBuilder.simple.optin.accepts'),
            refuse: t('pages.clients.queryBuilder.simple.optin.refuses'),
            noPreference: t('pages.clients.queryBuilder.simple.optin.noPreference'),
        };
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={String(value ?? BOOLEAN_NO_PREFERENCE_VALUE)}
                    onChange={(_event, newValue) => onChange(newValue ?? BOOLEAN_NO_PREFERENCE_VALUE)}
                >
                    <ToggleButton value={BOOLEAN_ACCEPT_VALUE} sx={ACCEPT_BUTTON_SX}>
                        {labels.accept}
                    </ToggleButton>
                    <ToggleButton value={BOOLEAN_REFUSE_VALUE} sx={REFUSE_BUTTON_SX}>
                        {labels.refuse}
                    </ToggleButton>
                    <ToggleButton value={BOOLEAN_NO_PREFERENCE_VALUE} sx={NO_PREFERENCE_BUTTON_SX}>
                        {labels.noPreference}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
        );
    }

    const handleOptionChange = (option: FieldValue, checked: boolean) => {
        const names = option.mergedNames ?? [option.name];
        const newValues = checked
            ? [...selectedValues.filter(v => !names.includes(v)), ...names]
            : selectedValues.filter(v => !names.includes(v));
        onChange(newValues);
    };

    if (compact) {
        return (
            <Box sx={COMPACT_GRID_SX}>
                {values.map((option, idx) => {
                    const isChecked = (option.mergedNames ?? [option.name]).some(name => selectedSet.has(name));
                    const label = option.tooltip ? (
                        <Box sx={TOOLTIP_LABEL_SX}>
                            {option.label}
                            <Tooltip title={option.tooltip} arrow>
                                <InfoOutlinedIcon sx={TOOLTIP_ICON_SX} />
                            </Tooltip>
                        </Box>
                    ) : option.label;

                    return (
                        <FormControlLabel
                            key={`${ruleId}-compact-${idx}`}
                            sx={FORM_CONTROL_LABEL_SX}
                            control={
                                <Checkbox
                                    checked={isChecked}
                                    onChange={(e) => handleOptionChange(option, e.target.checked)}
                                    size="small"
                                />
                            }
                            label={label}
                        />
                    );
                })}
            </Box>
        );
    }

    return (
        <ChipsSelectEditor
            ruleId={ruleId}
            values={values}
            selectedValues={selectedValues}
            onChange={onChange}
        />
    );
}

export default memo(CheckboxEditor);
